import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";

export type InterfaceLanguage = "en" | "zh-CN";

export const interfaceLanguageOptions: ReadonlyArray<{
  code: InterfaceLanguage;
  label: string;
}> = [
  { code: "en", label: "English" },
  { code: "zh-CN", label: "简体中文" }
];

const storageKey = "loomua:interface-language:v2";
const legacyStorageKey = "loomua:interface-language:v1";

function initialLanguage(): InterfaceLanguage {
  const stored = window.localStorage.getItem(storageKey);
  if (stored === "en" || stored === "zh-CN") return stored;
  const legacy = window.localStorage.getItem(legacyStorageKey);
  if (legacy === "en" || legacy === "zh-Hans") {
    const canonical = legacy === "zh-Hans" ? "zh-CN" : legacy;
    window.localStorage.setItem(storageKey, canonical);
    window.localStorage.removeItem(legacyStorageKey);
    return canonical;
  }
  return window.navigator.language.toLowerCase().startsWith("zh")
    ? "zh-CN"
    : "en";
}

type InterfaceLanguageContextValue = {
  language: InterfaceLanguage;
  setLanguage: (language: InterfaceLanguage) => void;
};

const InterfaceLanguageContext =
  createContext<InterfaceLanguageContextValue | null>(null);

export function InterfaceLanguageProvider({
  children
}: {
  children: ReactNode;
}) {
  const [language, setLanguageState] =
    useState<InterfaceLanguage>(initialLanguage);

  useEffect(() => {
    document.documentElement.lang = language === "zh-CN" ? "zh-CN" : "en";
  }, [language]);

  const value = useMemo<InterfaceLanguageContextValue>(
    () => ({
      language,
      setLanguage(nextLanguage) {
        window.localStorage.setItem(storageKey, nextLanguage);
        setLanguageState(nextLanguage);
      }
    }),
    [language]
  );

  return (
    <InterfaceLanguageContext.Provider value={value}>
      {children}
    </InterfaceLanguageContext.Provider>
  );
}

export function useInterfaceLanguage() {
  const context = useContext(InterfaceLanguageContext);
  if (!context) {
    throw new Error(
      "useInterfaceLanguage must be used inside InterfaceLanguageProvider"
    );
  }
  return context;
}

export function useOptionalInterfaceLanguage() {
  return useContext(InterfaceLanguageContext);
}
